import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import { Suspense, useCallback, useState } from 'react';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  addEdge,
  Controls,
  Node,
  Background,
  useReactFlow,
  getRectOfNodes,
  getTransformForBounds
} from 'reactflow';
import 'reactflow/dist/style.css';
import { styled } from '@mui/material/styles';
import { toPng } from 'html-to-image';
import { useTranslation } from 'react-i18next';

import { GenericModalV2 } from 'components/shared';
import useGetClientStatusList from 'hooks/client/useGetClientStatusList';
import { getOppositeColor } from 'helpers/utils';

import {
  errorService,
  noResultService,
  styles,
  ticketStatus
} from './constants';
import TicketInfoModal from './TicketInfoModal';
import { useGetGanttModal } from './hooks/useGetGanttModal';

const ListItem = styled('li')(({ theme }) => ({
  margin: theme.spacing(0)
}));

interface Props {
  isOpen: boolean;
  onClose: () => void;
  workFlowOrder?: {
    workFlowOrderId: number | string;
    workFlowOrderName: string;
    workFlowOrderKey?: string;
  };
  workFlow?: {
    workFlowId: number;
    workFlowName: string;
  };
}

function GanttModal({
  isOpen,
  onClose,
  workFlowOrder = null,
  workFlow = null
}: Props) {
  const [selectedNode, setSelectedNode] = useState<Node>(null);

  const { getNodes } = useReactFlow();
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { t } = useTranslation('global');
  const { statusList } = useGetClientStatusList();

  const getGanttModal = useGetGanttModal({
    workFlowOrderId: workFlowOrder?.workFlowOrderId,
    workFlowId: workFlow?.workFlowId,
    setNodes,
    setEdges,
    statusList
  });

  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => addEdge(params, eds));
    },
    [setEdges]
  );

  function downloadPDF(pdfDocGenerator) {
    pdfDocGenerator.getBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('ticket.messages.ganttPDFTitle')}${
        workFlowOrder?.workFlowOrderName || workFlow?.workFlowName
      }${
        workFlowOrder?.workFlowOrderKey ||
        getGanttModal?.data?.initialStepService?.stepTicket
          ?.ticketClientGroup ||
        workFlowOrder?.workFlowOrderId
          ? ' - '.concat(
              workFlowOrder?.workFlowOrderKey ||
                getGanttModal?.data?.initialStepService?.stepTicket
                  ?.ticketClientGroup ||
                String(workFlowOrder?.workFlowOrderId)
            )
          : ''
      }.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  const handleClick = async () => {
    const nodesBounds = getRectOfNodes(getNodes());
    const transform = getTransformForBounds(
      nodesBounds,
      // medidas para hoja a4,
      (Math.floor(nodes.length / 8) || 1) * 1684,
      (Math.floor(nodes.length / 8) || 1) * 1190,
      0.5,
      2
    );

    await toPng(document.querySelector('.react-flow__viewport'), {
      backgroundColor: 'white',
      width: (Math.floor(nodes.length / 8) || 1) * 1684,
      height: (Math.floor(nodes.length / 8) || 1) * 1190,
      style: {
        width: (Math.floor(nodes.length / 8) || 1) * 1684,
        height: (Math.floor(nodes.length / 8) || 1) * 1190,
        transform: `translate(${transform[0]}px, ${transform[1]}px) scale(${transform[2]})`
      }
    }).then((dataUrl) => {
      const { pdfMake } = window;
      pdfMake.vfs = window.pdfMake.vfs;

      const pdfDocGenerator = pdfMake.createPdf({
        pageOrientation: 'landscape',
        content: [
          {
            image: dataUrl,
            width: 842,
            height: 595
          }
        ],
        pageMargins: [0, 0, 0, 0]
      });
      downloadPDF(pdfDocGenerator);
    });
  };

  return (
    <GenericModalV2
      isOpen={isOpen}
      onClose={onClose}
      style={styles.modal}
      title={`${t('ticket.messages.ganttPDFTitle')}${
        workFlowOrder?.workFlowOrderName || workFlow?.workFlowName
      }`}
    >
      <Suspense
        fallback={
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <CircularProgress />
          </div>
        }
      >
        {!!selectedNode && workFlowOrder && (
          <TicketInfoModal
            isOpen={!!selectedNode}
            onClose={() => setSelectedNode(null)}
            ticketId={+(selectedNode?.id || 0)}
            workFlowOrderId={getGanttModal?.data?.id}
          />
        )}
        <div
          style={{
            height: `${workFlowOrder ? '600px' : '700px'}`,
            width: '100%',
            position: 'relative'
          }}
        >
          {getGanttModal.isLoading && <LinearProgress />}
          {getGanttModal.isError && onClose()}
          {!getGanttModal.isLoading && !getGanttModal.isError && (
            <>
              <Button
                color="error"
                onClick={() => handleClick()}
                sx={{ position: 'absolute', right: 60, top: 10, zIndex: 1000 }}
                variant="contained"
              >
                {t('ticket.messages.saveAsPDF')}
              </Button>
              <ReactFlow
                draggable={false}
                edges={edges}
                nodes={nodes}
                nodesConnectable={false}
                nodesDraggable={false}
                onConnect={onConnect}
                onEdgesChange={onEdgesChange}
                onNodeClick={(event, node) =>
                  node.selectable ? setSelectedNode(node) : null
                }
                fitView
              >
                <Background color="#aaa" gap={16} size={1} />
                <Controls position="top-right" showInteractive={false} />
              </ReactFlow>

              {workFlowOrder && (
                <Paper
                  component="ul"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    listStyle: 'none',
                    p: 0.2,
                    m: 0
                  }}
                >
                  {statusList
                    ?.concat(errorService, noResultService)
                    ?.sort((a, b) =>
                      t(a?.description)?.localeCompare(t(b.description))
                    )
                    ?.map((chip) => (
                      <ListItem key={chip.id}>
                        <Chip
                          label={t(chip.description)}
                          sx={{
                            backgroundColor: chip.hexColor || '#939393',
                            color: getOppositeColor(chip.hexColor || '#939393'),
                            margin: '0.5rem'
                          }}
                        />
                      </ListItem>
                    ))}
                </Paper>
              )}
            </>
          )}
        </div>
      </Suspense>
    </GenericModalV2>
  );
}

export default GanttModal;
